/**
 * Student distribution / summary API.
 * Base URL comes from the same gateway env as the rest of the app (see `gatewayBaseUrlFromEnv`).
 */

import axios from "axios";
import { getToken } from "./storageService";
import { gatewayBaseUrlFromEnv } from "../runtime/loadSharedDashboardConfig";

const DISTRIBUTION_API_BASE = gatewayBaseUrlFromEnv();

const distributionClient = axios.create({
  baseURL: DISTRIBUTION_API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

distributionClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function getClientId(): string {
  const direct = localStorage.getItem("auth_user_id") ?? localStorage.getItem("clientId");
  if (direct) return direct;
  try {
    const authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
    return authUser?.id ?? "";
  } catch {
    return "";
  }
}

export interface SummaryCardLocation {
  name: string;
  value: number;
}

export interface StudentSummaryCardItem {
  id?: string;
  title: string;
  value: string;
  subtitle: string;
  timestamp?: string;
  locations?: SummaryCardLocation[];
}

export interface StaffSummaryCardItem {
  id?: string;
  title: string;
  value: string;
  subtitle: string;
  timestamp?: string;
  locations?: SummaryCardLocation[];
}

/** Unified shape for GET `/distribution/summary-cards` and WS `summary_cards` payloads. */
export type DistributionSummaryCardItem = StudentSummaryCardItem;

/**
 * GET /distribution/summary-cards — auth via cookies (withCredentials on axios client).
 */
export async function getDistributionSummaryCardsRest(params: {
  type: string;
  from?: string;
  to?: string;
  location_id?: string;
  camera_id?: string;
}): Promise<DistributionSummaryCardItem[]> {
  const clientId = getClientId();
  if (!clientId) {
    return [];
  }
  const res = await distributionClient.get<{
    success?: boolean;
    data?: DistributionSummaryCardItem[];
  }>("/distribution/summary-cards", {
    params: {
      clientId,
      type: params.type,
      from: params.from ?? undefined,
      to: params.to ?? undefined,
      locationId: params.location_id ?? "",
      cameraId: params.camera_id ?? "",
    },
  });
  const data = res.data?.data;
  return Array.isArray(data) ? data : [];
}

export interface StudentAggregatePresencePoint {
  time: string;
  student: number;
}

export interface StudentAggregatePresenceResponse {
  response: StudentAggregatePresencePoint[];
}

export interface StaffAggregatePresencePoint {
  time: string;
  staff: number;
}

export interface StaffAggregatePresenceResponse {
  response: StaffAggregatePresencePoint[];
}

/** Unified chart point shape from GET `/distribution/aggregate-presence` and WS payloads. */
export type DistributionAggregatePresencePoint = [string, number];

/**
 * GET /distribution/aggregate-presence — auth via cookies (withCredentials on axios client).
 */
export async function getDistributionAggregatePresenceRest(params: {
  type: string;
  from?: string;
  to?: string;
  location_id?: string;
  camera_id?: string;
}): Promise<DistributionAggregatePresencePoint[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await distributionClient.get<{
    success?: boolean;
    data?: unknown;
  }>("/distribution/aggregate-presence", {
    params: {
      clientId,
      type: params.type,
      from: params.from ?? undefined,
      to: params.to ?? undefined,
      locationId: params.location_id ?? "",
      cameraId: params.camera_id ?? "",
    },
  });

  const raw = res.data?.data;
  const parsed = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((item): item is [string, number] => Array.isArray(item) && item.length >= 2)
    .map(([time, value]) => [String(time), Number(value)]);
}

function safeJsonParse(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    return [];
  }
}

export async function getStudentSummary(params?: {
  from?: string;
  to?: string;
  location_id?: string | string[];
  camera_id?: string | string[];
}): Promise<StudentSummaryCardItem[]> {
  const clientId = getClientId();
  if (!clientId) {
    return [];
  }
  const locationId = params?.location_id
    ? Array.isArray(params.location_id)
      ? params.location_id
      : [params.location_id]
    : [];
  const cameraId = params?.camera_id
    ? Array.isArray(params.camera_id)
      ? params.camera_id
      : [params.camera_id]
    : [];
  try {
    const res = await distributionClient.post<{ success?: boolean; data?: StudentSummaryCardItem[] }>(
      "/distribution/student/summary-cards",
      {
        clientId,
        from: params?.from ?? undefined,
        to: params?.to ?? undefined,
        locationId,
        cameraId,
      }
    );
    const data = res.data?.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function getStaffSummary(params?: {
  from?: string;
  to?: string;
  location_id?: string | string[];
  camera_id?: string | string[];
}): Promise<StaffSummaryCardItem[]> {
  const clientId = getClientId();
  if (!clientId) {
    return [];
  }
  const locationId = params?.location_id
    ? Array.isArray(params.location_id)
      ? params.location_id
      : [params.location_id]
    : [];
  const cameraId = params?.camera_id
    ? Array.isArray(params.camera_id)
      ? params.camera_id
      : [params.camera_id]
    : [];
  try {
    const res = await distributionClient.post<{ success?: boolean; data?: StaffSummaryCardItem[] }>(
      "/distribution/staff/summary-cards",
      {
        clientId,
        from: params?.from ?? undefined,
        to: params?.to ?? undefined,
        locationId,
        cameraId,
      }
    );
    const data = res.data?.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** API returns data as [[timestamp, value], ...] */
function mapAggregateApiToPoints(
  raw: unknown[]
): StudentAggregatePresencePoint[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is [string, number] => Array.isArray(item) && item.length >= 2)
    .map(([time, value]) => ({ time: String(time), student: Number(value) }));
}

function mapAggregateApiToStaffPoints(raw: unknown[]): StaffAggregatePresencePoint[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is [string, number] => Array.isArray(item) && item.length >= 2)
    .map(([time, value]) => ({ time: String(time), staff: Number(value) }));
}

export async function getStudentAggregatePresence(params?: {
  from?: string;
  to?: string;
  location_id?: string | string[];
  camera_id?: string | string[];
}): Promise<StudentAggregatePresencePoint[]> {
  const clientId = getClientId();
  if (!clientId) {
    return [];
  }
  const locationId = params?.location_id
    ? Array.isArray(params.location_id)
      ? params.location_id
      : [params.location_id]
    : [];
  const cameraId = params?.camera_id
    ? Array.isArray(params.camera_id)
      ? params.camera_id
      : [params.camera_id]
    : [];
  try {
    const res = await distributionClient.post<{
      success?: boolean;
      data?: [string, number][];
    }>("/distribution/student/aggregate-presence", {
      clientId,
      from: params?.from ?? undefined,
      to: params?.to ?? undefined,
      locationId,
      cameraId,
    });
    const raw = res.data?.data;
    return Array.isArray(raw) ? mapAggregateApiToPoints(raw) : [];
  } catch {
    return [];
  }
}

export async function getStaffAggregatePresence(params?: {
  from?: string;
  to?: string;
  location_id?: string | string[];
  camera_id?: string | string[];
}): Promise<StaffAggregatePresencePoint[]> {
  const clientId = getClientId();
  if (!clientId) {
    return [];
  }
  const locationId = params?.location_id
    ? Array.isArray(params.location_id)
      ? params.location_id
      : [params.location_id]
    : [];
  const cameraId = params?.camera_id
    ? Array.isArray(params.camera_id)
      ? params.camera_id
      : [params.camera_id]
    : [];
  try {
    const res = await distributionClient.post<{
      success?: boolean;
      data?: [string, number][];
    }>("/distribution/staff/aggregate-presence", {
      clientId,
      from: params?.from ?? undefined,
      to: params?.to ?? undefined,
      locationId,
      cameraId,
    });
    const raw = res.data?.data;
    return Array.isArray(raw) ? mapAggregateApiToStaffPoints(raw) : [];
  } catch {
    return [];
  }
}
